/**
 * Surface 5 — Collector Correction Requests Queue (Phase 3 UI).
 * See /app/memory/design_tax_collectors_ui.md §3 Surface 5.
 *
 * Feb 2026 — Redesigned to share the Jurisdiction Directory design
 * language across the Jurisdiction Management workspace. KPI cards,
 * unified header/filter/table styling. Underlying logic (status tabs,
 * detail sheet, approve/reject/duplicate flows) is unchanged.
 *
 * Access:
 *   • admin / jurisdiction_manager → full queue, can Approve / Reject / Mark Duplicate
 *   • other roles → blocked at the route level (backend enforces 403)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw, Filter, Search, AlertCircle, ExternalLink, Loader2, X,
  CheckCircle2, XCircle, Copy as CopyIcon, Flag, ChevronRight,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { CollectorResolvePanel } from '../components/jurisdiction/CollectorResolvePanel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { CollectorCorrectionModal } from '../components/jurisdiction/CollectorCorrectionModal';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { getUserFromToken } from '../utils/adminApi';
import {
  listCorrectionRequests,
  approveCorrectionRequest,
  rejectCorrectionRequest,
  markDuplicateCorrectionRequest,
  resolveCollectorGuidance,
} from '../utils/taxCollectorsApi';
import './JurisdictionDirectory.css';

const STATUS_TABS = [
  { key: 'pending',   label: 'Pending' },
  { key: 'approved',  label: 'Approved' },
  { key: 'rejected',  label: 'Rejected' },
  { key: 'duplicate', label: 'Duplicate' },
];

const ISSUE_TYPE_LABELS = {
  missing_collector:        'Missing collector',
  incorrect_name:           'Incorrect name',
  incorrect_address:        'Incorrect address',
  incorrect_phone:          'Incorrect phone',
  wrong_collector_entirely: 'Wrong collector entirely',
  other:                    'Other',
};

const PRIORITY_META = {
  high:   { dotClass: 'bg-red-500',     textClass: 'text-slate-900 font-medium', label: 'High' },
  medium: { dotClass: 'bg-yellow-500',  textClass: 'text-slate-900 font-medium', label: 'Medium' },
  low:    { dotClass: null,             textClass: 'text-slate-500',             label: 'Low' },
};

const STATUS_PILL_CLASS = {
  pending:   'bg-indigo-50 text-indigo-700 border-indigo-200',
  approved:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected:  'bg-red-50 text-red-700 border-red-200',
  duplicate: 'bg-slate-100 text-slate-600 border-slate-200',
};

function relTime(iso) {
  if (!iso) return '—';
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return '—'; }
}

function PriorityPill({ bucket }) {
  const meta = PRIORITY_META[bucket] || PRIORITY_META.low;
  if (bucket === 'low') {
    return (
      <span className={`text-xs ${meta.textClass}`} data-testid={`priority-${bucket}-pill`} aria-label={`Priority: ${meta.label}`}>
        {meta.label}
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs ${meta.textClass}`}
      data-testid={`priority-${bucket}-pill`}
      aria-label={`Priority: ${meta.label}`}
    >
      <span className={`h-2 w-2 rounded-full ${meta.dotClass}`} />
      {meta.label}
    </span>
  );
}

function StatusPill({ status }) {
  const cls = STATUS_PILL_CLASS[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}
      data-testid={`ccr-status-${status}`}
    >
      {status}
    </span>
  );
}

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS',
  'KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY',
  'NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV',
  'WI','WY','DC',
];

const CollectorCorrectionRequestsPage = ({ embedded = false } = {}) => {
  const navigate = useNavigate();
  const currentUser = useMemo(() => getUserFromToken(), []);
  const isReviewer = currentUser?.role === 'admin' || currentUser?.role === 'jurisdiction_manager';

  const [activeStatus, setActiveStatus] = useState('pending');
  const [filters, setFilters] = useState({ state: '', request_type: '', priority_bucket: '', search: '' });
  const [data, setData] = useState({ items: [], pagination: { total: 0, page: 1, total_pages: 0 } });
  const [statusCounts, setStatusCounts] = useState({ pending: 0, approved: 0, rejected: 0, duplicate: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Detail sheet
  const [sheet, setSheet] = useState({ open: false, row: null, guidance: null, loadingGuidance: false });

  // Action confirmations
  const [rejectDialog, setRejectDialog] = useState({ open: false, row: null, notes: '', submitting: false });
  const [duplicateDialog, setDuplicateDialog] = useState({ open: false, row: null, target_id: '', notes: '', submitting: false });
  const [approvingId, setApprovingId] = useState(null);

  // ── Data fetching ─────────────────────────────────────────────
  const fetchList = useCallback(async (status) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        status,
        page_size: 100,
        ...(filters.state ? { state: filters.state } : {}),
        ...(filters.request_type ? { request_type: filters.request_type } : {}),
        ...(filters.priority_bucket ? { priority_bucket: filters.priority_bucket } : {}),
      };
      const res = await listCorrectionRequests(params);
      setData(res || { items: [], pagination: { total: 0 } });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to load requests';
      setError(detail);
      setData({ items: [], pagination: { total: 0 } });
    } finally {
      setLoading(false);
    }
  }, [filters.state, filters.request_type, filters.priority_bucket]);

  const refreshStatusCounts = useCallback(async () => {
    try {
      const results = await Promise.all(
        STATUS_TABS.map((t) => listCorrectionRequests({ status: t.key, page_size: 1 })),
      );
      setStatusCounts({
        pending:   results[0]?.pagination?.total || 0,
        approved:  results[1]?.pagination?.total || 0,
        rejected:  results[2]?.pagination?.total || 0,
        duplicate: results[3]?.pagination?.total || 0,
      });
    } catch (err) {
      // Non-fatal; tab counts stay at last-known values
    }
  }, []);

  useEffect(() => { fetchList(activeStatus); }, [fetchList, activeStatus]);
  useEffect(() => { refreshStatusCounts(); }, [refreshStatusCounts]);

  // ── KPI dataset (independent of active tab & filters) ─────────
  // One small fetch per status (page_size=1) already powers the tab
  // badges. We additionally pull the most recent 200 items across all
  // statuses to compute "Recently Submitted (last 30 days)" without a
  // dedicated backend endpoint. Tiny dataset; negligible cost.
  const [recentItems, setRecentItems] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const res = await listCorrectionRequests({ page_size: 200 });
        setRecentItems(res?.items || []);
      } catch (_) { /* non-critical */ }
    })();
  }, [activeStatus]); // refresh when admin switches tabs (post-action churn)

  const kpis = useMemo(() => {
    const pending = statusCounts.pending || 0;
    const approved = statusCounts.approved || 0;
    const rejected = statusCounts.rejected || 0;
    const duplicate = statusCounts.duplicate || 0;
    const resolved = approved + rejected + duplicate;
    const total = pending + resolved;
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const last30 = recentItems.filter(r => {
      const t = r.submitted_at ? Date.parse(r.submitted_at) : 0;
      return t >= cutoff;
    }).length;
    return { open: pending, pending, resolved, total, last30 };
  }, [statusCounts, recentItems]);

  // ── Filtered + searched view ──────────────────────────────────
  const visibleItems = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    if (!q) return data.items;
    return data.items.filter((r) => {
      const jName = (r.jurisdiction?.jurisdiction_name || '').toLowerCase();
      const submitter = (r.submitted_by_name || '').toLowerCase();
      const desc = (r.description || '').toLowerCase();
      return jName.includes(q) || submitter.includes(q) || desc.includes(q);
    });
  }, [data.items, filters.search]);

  // ── Sheet handlers ────────────────────────────────────────────
  const openSheet = useCallback(async (row) => {
    setSheet({ open: true, row, guidance: null, loadingGuidance: true });
    try {
      const res = await resolveCollectorGuidance([{
        ref_id: row.id,
        jurisdiction_id: row.jurisdiction_id,
        tax_period: row.tax_period,
        tax_category_code: row.tax_category_code || null,
      }]);
      setSheet((s) => ({ ...s, guidance: (res?.guidance || [])[0] || null, loadingGuidance: false }));
    } catch (err) {
      setSheet((s) => ({ ...s, guidance: null, loadingGuidance: false }));
    }
  }, []);

  const closeSheet = useCallback(() => {
    setSheet({ open: false, row: null, guidance: null, loadingGuidance: false });
  }, []);

  // ── Actions ───────────────────────────────────────────────────
  const handleApprove = useCallback(async (row) => {
    if (!row?.id) return;
    setApprovingId(row.id);
    try {
      await approveCorrectionRequest(row.id, {
        resolution_action: 'Marked approved from queue. Edit collector / assignment manually as needed.',
      });
      toast.success('Request approved. Update the collector or assignment record next.');
      // After approve we open the Tax Collectors page in a new tab so the
      // admin can take the manual update step (no auto-create per §5.4).
      window.open('/admin/jurisdictions/tax-collectors', '_blank', 'noopener');
      await Promise.all([fetchList(activeStatus), refreshStatusCounts()]);
      closeSheet();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to approve');
    } finally {
      setApprovingId(null);
    }
  }, [activeStatus, fetchList, refreshStatusCounts, closeSheet]);

  const handleReject = useCallback(async () => {
    const { row, notes } = rejectDialog;
    if (!row?.id) return;
    if ((notes || '').trim().length < 5) {
      toast.error('Rejection notes must be at least 5 characters.');
      return;
    }
    setRejectDialog((s) => ({ ...s, submitting: true }));
    try {
      await rejectCorrectionRequest(row.id, notes.trim());
      toast.success('Request rejected');
      setRejectDialog({ open: false, row: null, notes: '', submitting: false });
      await Promise.all([fetchList(activeStatus), refreshStatusCounts()]);
      closeSheet();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reject');
      setRejectDialog((s) => ({ ...s, submitting: false }));
    }
  }, [rejectDialog, activeStatus, fetchList, refreshStatusCounts, closeSheet]);

  const handleMarkDuplicate = useCallback(async () => {
    const { row, target_id, notes } = duplicateDialog;
    if (!row?.id) return;
    if (!target_id.trim()) {
      toast.error('Enter the canonical request id.');
      return;
    }
    setDuplicateDialog((s) => ({ ...s, submitting: true }));
    try {
      await markDuplicateCorrectionRequest(row.id, {
        duplicate_of_id: target_id.trim(),
        review_notes: notes.trim() || null,
      });
      toast.success('Request marked as duplicate');
      setDuplicateDialog({ open: false, row: null, target_id: '', notes: '', submitting: false });
      await Promise.all([fetchList(activeStatus), refreshStatusCounts()]);
      closeSheet();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark duplicate');
      setDuplicateDialog((s) => ({ ...s, submitting: false }));
    }
  }, [duplicateDialog, activeStatus, fetchList, refreshStatusCounts, closeSheet]);

  // ── Permission gate ───────────────────────────────────────────
  if (!isReviewer) {
    return (
      <div className="mx-auto max-w-2xl p-6" data-testid="ccr-no-access">
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-amber-900">
          <h2 className="text-lg font-medium">Access restricted</h2>
          <p className="mt-1 text-sm">
            Collector Correction Requests are managed by Admin and Jurisdiction Manager
            roles only.
          </p>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────
  const hasFilters = !!(filters.search || filters.state || filters.request_type || filters.priority_bucket);
  const resetFilters = () => setFilters({ state: '', request_type: '', priority_bucket: '', search: '' });

  return (
    <div className={embedded ? 'jurisdiction-directory jurisdiction-directory--embedded' : 'jurisdiction-directory'} data-testid="collector-correction-requests-page">
      {/* Header + KPI cards are hidden when embedded in the consolidated Jurisdiction Requests hub (JURISDICTION-REQUESTS-CONSOLIDATION-001) */}
      {!embedded && (<>
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Collector Corrections</h1>
          <p className="page-subtitle">
            Review collector data flags submitted by processors. Approving opens the
            Tax Collectors editor for a manual fix — nothing is auto-created.
          </p>
        </div>
        <div className="page-header-right">
          <Button
            variant="outline"
            onClick={() => { fetchList(activeStatus); refreshStatusCounts(); }}
            data-testid="ccr-refresh-btn"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="kpi-grid" data-testid="ccr-kpi-grid">
        <div className="kpi-card kpi-accent-amber" data-testid="kpi-open-corrections">
          <p className="kpi-label">Open Corrections</p>
          <div className="kpi-value">{kpis.open}</div>
          <p className="kpi-hint">Awaiting reviewer action</p>
        </div>
        <div className="kpi-card kpi-accent-indigo" data-testid="kpi-pending-review">
          <p className="kpi-label">Pending Review</p>
          <div className="kpi-value">{kpis.pending}</div>
          <p className="kpi-hint">In the queue right now</p>
        </div>
        <div className="kpi-card kpi-accent-emerald" data-testid="kpi-resolved">
          <p className="kpi-label">Resolved</p>
          <div className="kpi-value">{kpis.resolved}</div>
          <p className="kpi-hint">Approved · Rejected · Duplicate</p>
        </div>
        <div className="kpi-card kpi-accent-sky" data-testid="kpi-recent-30d">
          <p className="kpi-label">Recently Submitted</p>
          <div className="kpi-value">{kpis.last30}</div>
          <p className="kpi-hint">Last 30 days</p>
        </div>
      </div>

      </>)}
      {/* Status tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-4" data-testid="ccr-status-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActiveStatus(t.key)}
            className={
              `relative px-4 py-2 text-sm font-medium transition-colors ` +
              (activeStatus === t.key
                ? 'text-indigo-700 after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-indigo-600'
                : 'text-slate-500 hover:text-slate-700')
            }
            data-testid={`ccr-tab-${t.key}`}
          >
            <span>{t.label}</span>
            <span className="ml-1.5 inline-flex items-center rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
              {statusCounts[t.key] || 0}
            </span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-input-wrap">
          <Search className="search-icon w-4 h-4" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            placeholder="Search jurisdiction, submitter, or description…"
            className="search-input"
            data-testid="ccr-search-input"
          />
        </div>
        <select
          value={filters.state}
          onChange={(e) => setFilters((f) => ({ ...f, state: e.target.value }))}
          className="filter-select"
          data-testid="ccr-filter-state"
        >
          <option value="">All States</option>
          {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={filters.request_type}
          onChange={(e) => setFilters((f) => ({ ...f, request_type: e.target.value }))}
          className="filter-select"
          data-testid="ccr-filter-type"
        >
          <option value="">All Issue Types</option>
          {Object.entries(ISSUE_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select
          value={filters.priority_bucket}
          onChange={(e) => setFilters((f) => ({ ...f, priority_bucket: e.target.value }))}
          className="filter-select"
          data-testid="ccr-filter-priority"
        >
          <option value="">All Priorities</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        {hasFilters && (
          <Button variant="outline" onClick={resetFilters} data-testid="ccr-reset-filters">
            <X className="w-4 h-4" /> Reset
          </Button>
        )}
      </div>

      {/* Stats */}
      {!loading && !error && (
        <div className="stats-bar">
          <span className="stat-item">
            <strong>{visibleItems.length}</strong>{' '}
            {visibleItems.length === 1 ? 'request' : 'requests'}
            {' · '}
            <span style={{ textTransform: 'capitalize' }}>{activeStatus}</span>
          </span>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="loading-state" data-testid="ccr-loading">
          <div className="spinner"></div>
          <p>Loading requests…</p>
        </div>
      ) : error ? (
        <div className="error-state" data-testid="ccr-error">
          <p className="error-message"><AlertCircle className="inline w-4 h-4 mr-1" /> {error}</p>
          <Button variant="outline" onClick={() => { fetchList(activeStatus); refreshStatusCounts(); }}>
            Retry
          </Button>
        </div>
      ) : visibleItems.length === 0 ? (
        <div className="empty-state" data-testid="ccr-empty">
          <div className="empty-state-icon">🚩</div>
          <h3 className="empty-state-title">No {activeStatus} requests</h3>
          <p className="empty-state-description">
            {hasFilters
              ? 'Try adjusting your filters to see more results.'
              : 'New collector correction requests will appear here.'}
          </p>
        </div>
      ) : (
        <div className="table-container">
          <table className="jurisdictions-table">
            <thead>
              <tr>
                <th>Jurisdiction</th>
                <th>Issue Type</th>
                <th style={{ textAlign: 'right' }}>Affected Orders</th>
                <th>Priority</th>
                <th>Submitted By</th>
                <th>Submitted</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((r) => (
                <tr
                  key={r.id}
                  style={{ cursor: 'pointer' }}
                  data-testid={`ccr-row-${r.id}`}
                  onClick={() => openSheet(r)}
                >
                  <td>
                    <div className="jurisdiction-name">{r.jurisdiction?.jurisdiction_name || r.jurisdiction_id}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      {r.jurisdiction?.state || '—'} · {r.tax_period}{r.tax_category_code ? ` · ${r.tax_category_code}` : ''}
                    </div>
                  </td>
                  <td style={{ color: '#334155' }}>{ISSUE_TYPE_LABELS[r.request_type] || r.request_type}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#0f172a' }}>
                    {r.affected_orders_count || 0}
                  </td>
                  <td><PriorityPill bucket={r.priority_bucket || 'low'} /></td>
                  <td style={{ color: '#334155' }}>{r.submitted_by_name || r.submitted_by_id}</td>
                  <td style={{ fontSize: '13px', color: '#6b7280' }} title={r.submitted_at}>{relTime(r.submitted_at)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <ChevronRight className="ml-auto h-4 w-4 text-slate-400" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* COLLECTOR-CORRECTION-PREMIUM-UI-001 — shared centered premium modal (handlers/testids unchanged) */}
      <CollectorCorrectionModal
        open={sheet.open}
        onOpenChange={(open) => { if (!open) closeSheet(); }}
        row={sheet.row}
        testId="ccr-detail-sheet"
        footer={sheet.row && (
          <div className="ccr-footer justify-end">
            {sheet.row.status === 'pending' && sheet.row.source !== 'pa_auto_assign' && (<>
              <Button variant="ghost" onClick={() => setRejectDialog({ open: true, row: sheet.row, notes: '', submitting: false })} data-testid="ccr-reject-btn"><XCircle className="mr-1 h-4 w-4" /> Reject</Button>
              <Button variant="ghost" onClick={() => setDuplicateDialog({ open: true, row: sheet.row, target_id: '', notes: '', submitting: false })} data-testid="ccr-mark-duplicate-btn"><CopyIcon className="mr-1 h-4 w-4" /> Mark duplicate</Button>
              <Button className="ccr-cta" onClick={() => handleApprove(sheet.row)} disabled={approvingId === sheet.row.id} data-testid="ccr-approve-btn">
                {approvingId === sheet.row.id ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Approving…</> : <><CheckCircle2 className="mr-1 h-4 w-4" /> Approve &amp; open Collector form</>}
              </Button>
            </>)}
            {sheet.row.status !== 'pending' && <Button variant="outline" onClick={closeSheet} data-testid="ccr-close-btn">Close</Button>}
          </div>
        )}
      >
        {sheet.row && sheet.row.source !== 'pa_auto_assign' && (
          <section className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="ccr-resolver-state">
            <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Current resolver state</h3>
            {sheet.loadingGuidance ? <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Resolving…</div>
              : sheet.guidance ? (<><Field label="Resolved collector" value={sheet.guidance.collector?.display_name} /><Field label="Phone" value={sheet.guidance.collector?.phone} /><Field label="Mailing address" value={sheet.guidance.collector?.mailing_address} multiline /></>)
              : <div className="text-xs text-slate-500">Could not resolve.</div>}
          </section>
        )}
        {sheet.row && sheet.row.source === 'pa_auto_assign' && sheet.row.status === 'pending' && (
          <CollectorResolvePanel row={sheet.row} onResolved={async () => { await Promise.all([fetchList(activeStatus), refreshStatusCounts()]); }} />
        )}
      </CollectorCorrectionModal>

      {/* Reject dialog */}
      <AlertDialog
        open={rejectDialog.open}
        onOpenChange={(open) => { if (!open) setRejectDialog({ open: false, row: null, notes: '', submitting: false }); }}
      >
        <AlertDialogContent data-testid="ccr-reject-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reject this correction request?</AlertDialogTitle>
            <AlertDialogDescription>
              The submitter will see the rejection reason in their notifications.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="ccr-reject-notes">Rejection notes <span className="text-red-500">*</span> <span className="text-xs text-slate-500">(min 5 chars)</span></Label>
            <Textarea
              id="ccr-reject-notes"
              rows={3}
              value={rejectDialog.notes}
              onChange={(e) => setRejectDialog((s) => ({ ...s, notes: e.target.value }))}
              placeholder="e.g. 'Verified correct against PA workbook v2026-Q2.'"
              data-testid="ccr-reject-notes-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={rejectDialog.submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={rejectDialog.submitting || rejectDialog.notes.trim().length < 5}
              onClick={handleReject}
              data-testid="ccr-reject-confirm-btn"
            >
              {rejectDialog.submitting ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Rejecting…</> : 'Reject'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mark duplicate dialog */}
      <AlertDialog
        open={duplicateDialog.open}
        onOpenChange={(open) => { if (!open) setDuplicateDialog({ open: false, row: null, target_id: '', notes: '', submitting: false }); }}
      >
        <AlertDialogContent data-testid="ccr-duplicate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark as duplicate</AlertDialogTitle>
            <AlertDialogDescription>
              Provide the ID of the canonical pending request that this one duplicates.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="ccr-dup-target">Canonical request ID <span className="text-red-500">*</span></Label>
              <Input
                id="ccr-dup-target"
                value={duplicateDialog.target_id}
                onChange={(e) => setDuplicateDialog((s) => ({ ...s, target_id: e.target.value }))}
                placeholder="e.g. f47ac10b-58cc-4372-a567-0e02b2c3d479"
                data-testid="ccr-dup-target-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ccr-dup-notes">Notes (optional)</Label>
              <Textarea
                id="ccr-dup-notes"
                rows={2}
                value={duplicateDialog.notes}
                onChange={(e) => setDuplicateDialog((s) => ({ ...s, notes: e.target.value }))}
                data-testid="ccr-dup-notes-input"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={duplicateDialog.submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={duplicateDialog.submitting || !duplicateDialog.target_id.trim()}
              onClick={handleMarkDuplicate}
              data-testid="ccr-dup-confirm-btn"
            >
              {duplicateDialog.submitting ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Marking…</> : 'Mark duplicate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ── Small presentational helpers ────────────────────────────────
const Field = ({ label, value, multiline = false }) => (
  <div className="mt-1.5">
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    {multiline ? (
      <div className="whitespace-pre-wrap text-sm text-slate-800">{value || '—'}</div>
    ) : (
      <div className="text-sm text-slate-800">{value || '—'}</div>
    )}
  </div>
);


export default CollectorCorrectionRequestsPage;
